import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Slider,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  AddCircleOutline as AddIcon,
  ContentCopy as DuplicateIcon,
  TextFields as TextIcon,
  Image as ImageIcon,
  QrCode as BarcodeIcon,
  QrCode2 as QrCodeIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatColorFill as ColorIcon,
  FormatSize as FontSizeIcon,
  BorderAll as BorderIcon
} from '@mui/icons-material';

export interface LabelTemplate {
  id: string;
  name: string;
  type: 'shelf' | 'item' | 'barcode' | 'custom';
  width: number;  // in inches
  height: number; // in inches
  elements: LabelElement[];
  isDefault?: boolean;
}

export interface LabelElement {
  id: string;
  type: 'text' | 'barcode' | 'qrcode' | 'image' | 'line' | 'rectangle';
  x: number;
  y: number;
  width: number;
  height: number;
  content?: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  color?: string;
  backgroundColor?: string;
  borderWidth?: number;
  borderColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  rotation?: number;
  dataSource?: string; // e.g. "product.name", "product.price", "product.sku"
}

interface LabelDesignerProps {
  template: LabelTemplate;
  onSave: (template: LabelTemplate) => void;
  onDelete?: (templateId: string) => void;
  showControls?: boolean;
}

const LabelDesigner: React.FC<LabelDesignerProps> = ({
  template,
  onSave,
  onDelete,
  showControls = true
}) => {
  const [editingTemplate, setEditingTemplate] = useState<LabelTemplate>({...template});
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState(template.name);
  const [activeTab, setActiveTab] = useState<'design' | 'properties'>('design');
  
  // Calculate scaling for the preview
  const scale = Math.min(300 / (editingTemplate.width * 96), 300 / (editingTemplate.height * 96));
  
  // Get currently selected element
  const getSelectedElementData = () => {
    if (!selectedElement) return null;
    return editingTemplate.elements.find(el => el.id === selectedElement) || null;
  };
  
  // Handle updating template name
  const handleSaveTemplateName = () => {
    setEditingTemplate({
      ...editingTemplate,
      name: newTemplateName
    });
    setNameDialogOpen(false);
  };
  
  // Handle template property changes
  const handlePropertyChange = (property: keyof LabelTemplate, value: any) => {
    setEditingTemplate({
      ...editingTemplate,
      [property]: value
    });
  };
  
  // Handle element property changes
  const handleElementPropertyChange = (elementId: string, property: keyof LabelElement, value: any) => {
    setEditingTemplate({
      ...editingTemplate,
      elements: editingTemplate.elements.map(el => 
        el.id === elementId ? { ...el, [property]: value } : el
      )
    });
  };
  
  // Add new element
  const handleAddElement = (type: LabelElement['type']) => {
    const newElement: LabelElement = {
      id: `element_${Date.now()}`,
      type,
      x: 10,
      y: 10,
      width: type === 'text' ? 100 : 50,
      height: type === 'text' ? 30 : 50,
      content: type === 'text' ? 'New Text' : '',
      fontSize: 12,
      fontFamily: 'Arial',
      color: '#000000',
      textAlign: 'left'
    };
    
    if (type === 'barcode' || type === 'qrcode') {
      newElement.content = '12345';
      newElement.dataSource = 'product.sku';
    }
    
    setEditingTemplate({
      ...editingTemplate,
      elements: [...editingTemplate.elements, newElement]
    });
    
    setSelectedElement(newElement.id);
  };
  
  // Delete selected element
  const handleDeleteElement = () => {
    if (!selectedElement) return;
    
    setEditingTemplate({
      ...editingTemplate,
      elements: editingTemplate.elements.filter(el => el.id !== selectedElement)
    });
    
    setSelectedElement(null);
  };
  
  // Duplicate selected element
  const handleDuplicateElement = () => {
    if (!selectedElement) return;
    
    const element = getSelectedElementData();
    if (!element) return;
    
    const newElement = {
      ...element,
      id: `element_${Date.now()}`,
      x: element.x + 10,
      y: element.y + 10
    };
    
    setEditingTemplate({
      ...editingTemplate,
      elements: [...editingTemplate.elements, newElement]
    });
    
    setSelectedElement(newElement.id);
  };
  
  // Handle saving the template
  const handleSave = () => {
    onSave(editingTemplate);
  };
  
  // Handle deleting the template
  const handleDelete = () => {
    if (onDelete && window.confirm('Are you sure you want to delete this template?')) {
      onDelete(editingTemplate.id);
    }
  };
  
  // Render element in the designer
  const renderElement = (element: LabelElement) => {
    const isSelected = element.id === selectedElement;
    
    // Common styles for all elements
    const commonStyles = {
      position: 'absolute',
      left: `${element.x}px`,
      top: `${element.y}px`,
      width: `${element.width}px`,
      height: `${element.height}px`,
      transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
      border: isSelected ? '1px dashed blue' : 'none',
      cursor: 'pointer',
      backgroundColor: element.backgroundColor || 'transparent',
      boxSizing: 'border-box',
      overflow: 'hidden'
    };
    
    switch (element.type) {
      case 'text':
        return (
          <Box
            key={element.id}
            onClick={() => setSelectedElement(element.id)}
            sx={{
              ...commonStyles,
              fontFamily: element.fontFamily,
              fontSize: `${element.fontSize}px`,
              fontWeight: element.fontWeight,
              fontStyle: element.fontStyle,
              color: element.color,
              textAlign: element.textAlign,
              display: 'flex',
              alignItems: 'center',
              padding: '2px'
            }}
          >
            {element.content}
          </Box>
        );
        
      case 'barcode':
        return (
          <Box
            key={element.id}
            onClick={() => setSelectedElement(element.id)}
            sx={{
              ...commonStyles,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <BarcodeIcon sx={{ width: '100%', height: '70%' }} />
            <Typography variant="caption" sx={{ mt: 0.5 }}>
              {element.content || 'Barcode'}
            </Typography>
          </Box>
        );
        
      case 'qrcode':
        return (
          <Box
            key={element.id}
            onClick={() => setSelectedElement(element.id)}
            sx={{
              ...commonStyles,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <QrCodeIcon sx={{ width: '80%', height: '80%' }} />
            <Typography variant="caption" sx={{ mt: 0.5 }}>
              {element.content || 'QR Code'}
            </Typography>
          </Box>
        );
        
      case 'image':
        return (
          <Box
            key={element.id}
            onClick={() => setSelectedElement(element.id)}
            sx={{
              ...commonStyles,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ImageIcon sx={{ width: '50%', height: '50%', opacity: 0.5 }} />
          </Box>
        );
        
      case 'rectangle':
        return (
          <Box
            key={element.id}
            onClick={() => setSelectedElement(element.id)}
            sx={{
              ...commonStyles,
              border: `${element.borderWidth || 1}px solid ${element.borderColor || '#000'}`
            }}
          />
        );
        
      case 'line':
        return (
          <Box
            key={element.id}
            onClick={() => setSelectedElement(element.id)}
            sx={{
              ...commonStyles,
              height: '1px',
              backgroundColor: element.color || '#000'
            }}
          />
        );
        
      default:
        return null;
    }
  };
  
  // Render property editor for the selected element
  const renderPropertyEditor = () => {
    const element = getSelectedElementData();
    if (!element) return null;
    
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2">Element Properties</Typography>
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="X Position"
            type="number"
            size="small"
            fullWidth
            value={element.x}
            onChange={(e) => handleElementPropertyChange(element.id, 'x', parseInt(e.target.value))}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Y Position"
            type="number"
            size="small"
            fullWidth
            value={element.y}
            onChange={(e) => handleElementPropertyChange(element.id, 'y', parseInt(e.target.value))}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Width"
            type="number"
            size="small"
            fullWidth
            value={element.width}
            onChange={(e) => handleElementPropertyChange(element.id, 'width', parseInt(e.target.value))}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Height"
            type="number"
            size="small"
            fullWidth
            value={element.height}
            onChange={(e) => handleElementPropertyChange(element.id, 'height', parseInt(e.target.value))}
          />
        </Grid>
        {element.type === 'text' && (
          <>
            <Grid item xs={12}>
              <TextField
                label="Text Content"
                size="small"
                fullWidth
                value={element.content}
                onChange={(e) => handleElementPropertyChange(element.id, 'content', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Text Source</InputLabel>
                <Select
                  value={element.dataSource || ''}
                  label="Text Source"
                  onChange={(e) => handleElementPropertyChange(element.id, 'dataSource', e.target.value)}
                >
                  <MenuItem value="">Static Text</MenuItem>
                  <MenuItem value="product.name">Product Name</MenuItem>
                  <MenuItem value="product.price">Product Price</MenuItem>
                  <MenuItem value="product.sku">Product SKU</MenuItem>
                  <MenuItem value="product.category">Product Category</MenuItem>
                  <MenuItem value="product.location">Product Location</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Font Family</InputLabel>
                <Select
                  value={element.fontFamily || 'Arial'}
                  label="Font Family"
                  onChange={(e) => handleElementPropertyChange(element.id, 'fontFamily', e.target.value)}
                >
                  <MenuItem value="Arial">Arial</MenuItem>
                  <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                  <MenuItem value="Courier New">Courier New</MenuItem>
                  <MenuItem value="Georgia">Georgia</MenuItem>
                  <MenuItem value="Verdana">Verdana</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Font Size"
                type="number"
                size="small"
                fullWidth
                value={element.fontSize}
                onChange={(e) => handleElementPropertyChange(element.id, 'fontSize', parseInt(e.target.value))}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ToggleButtonGroup
                  value={element.fontWeight}
                  exclusive
                  size="small"
                  onChange={(_, val) => handleElementPropertyChange(element.id, 'fontWeight', val)}
                >
                  <ToggleButton value="bold">
                    <BoldIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
                
                <ToggleButtonGroup
                  value={element.fontStyle}
                  exclusive
                  size="small"
                  onChange={(_, val) => handleElementPropertyChange(element.id, 'fontStyle', val)}
                >
                  <ToggleButton value="italic">
                    <ItalicIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
                
                <ToggleButtonGroup
                  value={element.textAlign}
                  exclusive
                  size="small"
                  onChange={(_, val) => handleElementPropertyChange(element.id, 'textAlign', val)}
                >
                  <ToggleButton value="left">Left</ToggleButton>
                  <ToggleButton value="center">Center</ToggleButton>
                  <ToggleButton value="right">Right</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
          </>
        )}
        {element.type === 'barcode' || element.type === 'qrcode' ? (
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Data Source</InputLabel>
              <Select
                value={element.dataSource || 'product.sku'}
                label="Data Source"
                onChange={(e) => handleElementPropertyChange(element.id, 'dataSource', e.target.value)}
              >
                <MenuItem value="product.sku">Product SKU</MenuItem>
                <MenuItem value="product.id">Product ID</MenuItem>
                <MenuItem value="product.upc">Product UPC</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        ) : null}
        {(element.type === 'rectangle' || element.type === 'line') && (
          <>
            <Grid item xs={6}>
              <TextField
                label="Border Width"
                type="number"
                size="small"
                fullWidth
                value={element.borderWidth || 1}
                onChange={(e) => handleElementPropertyChange(element.id, 'borderWidth', parseInt(e.target.value))}
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Border Color"
                type="color"
                size="small"
                fullWidth
                value={element.borderColor || '#000000'}
                onChange={(e) => handleElementPropertyChange(element.id, 'borderColor', e.target.value)}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button 
              variant="outlined" 
              size="small" 
              color="error" 
              startIcon={<DeleteIcon />}
              onClick={handleDeleteElement}
            >
              Delete
            </Button>
            
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<DuplicateIcon />}
              onClick={handleDuplicateElement}
            >
              Duplicate
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  };
  
  // Render template properties
  const renderTemplateProperties = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2">Template Properties</Typography>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>Template Name:</Typography>
            <Typography fontWeight="bold">{editingTemplate.name}</Typography>
            <IconButton size="small" onClick={() => {
              setNewTemplateName(editingTemplate.name);
              setNameDialogOpen(true);
            }}>
              <TextIcon fontSize="small" />
            </IconButton>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Width (inches)"
            type="number"
            size="small"
            fullWidth
            value={editingTemplate.width}
            onChange={(e) => handlePropertyChange('width', Number(e.target.value))}
            inputProps={{ step: 0.1, min: 0.5 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Height (inches)"
            type="number"
            size="small"
            fullWidth
            value={editingTemplate.height}
            onChange={(e) => handlePropertyChange('height', Number(e.target.value))}
            inputProps={{ step: 0.1, min: 0.5 }}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Template Type</InputLabel>
            <Select
              value={editingTemplate.type}
              label="Template Type"
              onChange={(e) => handlePropertyChange('type', e.target.value)}
            >
              <MenuItem value="shelf">Shelf Label</MenuItem>
              <MenuItem value="item">Item Label</MenuItem>
              <MenuItem value="barcode">Barcode Label</MenuItem>
              <MenuItem value="custom">Custom Label</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      {showControls && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <ToggleButtonGroup
              value={activeTab}
              exclusive
              onChange={(_, val) => val && setActiveTab(val)}
              size="small"
            >
              <ToggleButton value="design">Design</ToggleButton>
              <ToggleButton value="properties">Properties</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          <Box>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{ mr: 1 }}
            >
              Save Template
            </Button>
            
            {onDelete && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {/* Label Design Canvas */}
          <Paper
            elevation={2}
            sx={{ 
              p: 1, 
              backgroundColor: '#f5f5f5', 
              height: '100%',
              minHeight: 350
            }}
          >
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Label Canvas</Typography>
            </Box>
            
            {/* Toolbar */}
            {activeTab === 'design' && (
              <Box sx={{ mb: 1 }}>
                <Tooltip title="Add Text">
                  <IconButton size="small" onClick={() => handleAddElement('text')}>
                    <TextIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Add Barcode">
                  <IconButton size="small" onClick={() => handleAddElement('barcode')}>
                    <BarcodeIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Add QR Code">
                  <IconButton size="small" onClick={() => handleAddElement('qrcode')}>
                    <QrCodeIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Add Rectangle">
                  <IconButton size="small" onClick={() => handleAddElement('rectangle')}>
                    <BorderIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Add Image">
                  <IconButton size="small" onClick={() => handleAddElement('image')}>
                    <ImageIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            
            {/* Label Preview Area */}
            <Box sx={{ 
              position: 'relative',
              width: `${editingTemplate.width * 96 * scale}px`, 
              height: `${editingTemplate.height * 96 * scale}px`,
              margin: '0 auto',
              backgroundColor: 'white',
              border: '1px solid #ccc',
              boxShadow: '0 0 5px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              {editingTemplate.elements.map(renderElement)}
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          {/* Properties Panel */}
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            {activeTab === 'design' ? (
              getSelectedElementData() ? (
                renderPropertyEditor()
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Select an element to edit its properties
                </Typography>
              )
            ) : (
              renderTemplateProperties()
            )}
          </Paper>
        </Grid>
      </Grid>
      {/* Template Name Dialog */}
      <Dialog open={nameDialogOpen} onClose={() => setNameDialogOpen(false)}>
        <DialogTitle>Edit Template Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveTemplateName}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabelDesigner; 